import { createElement, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  Chip,
  Select,
  MenuItem,
} from '@mui/material';
import { Menu as MenuIcon, Dashboard as DashboardIcon, Group as GroupIcon, People as PeopleIcon, Leaderboard as LeaderboardIcon, Receipt as ReceiptIcon, Event as EventIcon, Spa as SpaIcon, CardMembership as CardMembershipIcon, Settings as SettingsIcon, Logout as LogoutIcon, QrCodeScanner as QrCodeScannerIcon, Language as LanguageIcon, Palette as PaletteIcon, AssignmentTurnedIn as AssignmentTurnedInIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../providers/TenantProvider';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import type { UserRole } from '../lib/db/types';
import type { MenuFeature } from '../lib/planFeatures';
import { canAccessMenuFeature } from '../lib/planFeatures';
import { features } from '../features';

const DRAWER_WIDTH = 260;
const staffNavigationRoles: UserRole[] = ['owner', 'manager', 'staff', 'coach'];

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  text: string;
  Icon: unknown;
  path: string;
  roles?: UserRole[]; // If specified, only show for these roles
  feature?: MenuFeature; // If specified, show only when the active plan includes this feature
  canAccess?: () => boolean; // If specified, only show if returns true
  // children?: NavItem[]; // Optional submenu items
}

type MuiIconModule =
  | ComponentType<SvgIconProps>
  | { default?: ComponentType<SvgIconProps> | { default?: ComponentType<SvgIconProps> } };

function resolveIcon(icon: unknown): ComponentType<SvgIconProps> {
  let candidate = icon as MuiIconModule | undefined;

  for (let index = 0; index < 4; index += 1) {
    if (typeof candidate === 'function') {
      return candidate;
    }

    if (candidate && typeof candidate === 'object' && '$$typeof' in candidate) {
      return candidate as unknown as ComponentType<SvgIconProps>;
    }

    if (candidate && typeof candidate === 'object' && 'default' in candidate) {
      candidate = candidate.default as MuiIconModule | undefined;
      continue;
    }

    break;
  }

  return resolveIcon(DashboardIcon);
}

function renderIcon(icon: unknown, props?: SvgIconProps): ReactNode {
  return createElement(resolveIcon(icon), props);
}

// Base navigation items with plan feature mapping.
const baseNavigationItems: NavItem[] = [
  {
    text: 'Dashboard',
    Icon: DashboardIcon,
    path: '/dashboard',
    roles: staffNavigationRoles,
    feature: 'dashboard',
  },
  {
    text: 'Check-In',
    Icon: QrCodeScannerIcon,
    path: '/checkin',
    roles: staffNavigationRoles,
    feature: 'checkin',
  },
  {
    text: 'Leads',
    Icon: LeaderboardIcon,
    path: '/leads',
    roles: staffNavigationRoles,
    feature: 'leads',
  },
  {
    text: 'Members',
    Icon: GroupIcon,
    path: '/members',
    roles: staffNavigationRoles,
    feature: 'members',
  },
  {
    text: 'Waivers',
    Icon: AssignmentTurnedInIcon,
    path: '/waivers',
    roles: staffNavigationRoles,
    feature: 'waivers',
  },
  {
    text: 'Invoices',
    Icon: ReceiptIcon,
    path: '/invoices',
    roles: staffNavigationRoles,
    feature: 'invoices',
  },
  {
    text: 'Reports',
    Icon: AssessmentIcon,
    path: '/reports',
    roles: ['owner', 'manager'],
    feature: 'reports',
  },
  {
    text: 'Appointments',
    Icon: EventIcon,
    path: '/appointments',
    roles: staffNavigationRoles,
    feature: 'appointments',
  },
  {
    text: 'Services',
    Icon: SpaIcon,
    path: '/services',
    roles: ['owner', 'manager'],
    feature: 'services',
  },
  {
    text: 'Plans',
    Icon: CardMembershipIcon,
    path: '/plans',
    roles: ['owner', 'manager'],
    feature: 'plans',
  },
  {
    text: 'Users',
    Icon: PeopleIcon,
    path: '/users',
    roles: ['owner'],
    feature: 'users',
  },
  {
    text: 'Settings',
    Icon: SettingsIcon,
    path: '/settings/general',
    roles: ['owner', 'manager'],
    feature: 'settings',
  },
  ...(features.uiKit
    ? [
        {
          text: 'UI Kit',
          Icon: PaletteIcon,
          path: '/ui-kit',
          roles: staffNavigationRoles,
        },
      ]
    : []),
];

export default function Layout({ children }: LayoutProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, activeTenant } = useTenant();
  const { roles, tenants, tenantId, switchTenant } = useTenant();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Responsive: drawer is permanent on desktop, temporary on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const canAccessItem = (item: NavItem) => {
    // Check roles first
    // Compatibility equivalent: if (item.roles && !item.roles.includes(role || ''))
    if (item.roles && (!role || !item.roles.includes(role))) {
      if (!item.roles.some((allowedRole) => roles.includes(allowedRole))) {
        return false;
      }
    }
    if (item.feature && !canAccessMenuFeature(activeTenant?.planId, item.feature)) {
      return false;
    }
    // Check custom canAccess function
    if (item.canAccess && !item.canAccess()) {
      return false;
    }
    return true;
  };

  const plansIndex = baseNavigationItems.findIndex((item) => item.text === 'Plans');
  const websiteInsertIndex = plansIndex >= 0 ? plansIndex + 1 : baseNavigationItems.length;

  // Build navigation items with Website placed next to setup/billing tools.
  const navigationItems: NavItem[] = [
    ...baseNavigationItems.slice(0, websiteInsertIndex),
    {
      text: 'Website',
      Icon: LanguageIcon,
      path: '/website',
      roles: ['owner'],
      feature: 'website',
    },
    ...baseNavigationItems.slice(websiteInsertIndex),
  ];

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'owner':
        return 'primary';
      case 'manager':
        return 'secondary';
      case 'staff':
        return 'default';
      default:
        return 'default';
    }
  };

  const logoUrl = activeTenant?.branding?.logoUrl || '/logo.png';

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#000' }}>
      {/* Logo / Brand */}
      <Box sx={{ p: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={logoUrl}
          alt="Logo"
          sx={{
            width: 80,
            height: 80,
            backgroundColor: 'white',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 2, py: 2 }}>
        {navigationItems.map((item) => {
          if (!canAccessItem(item)) return null;

          const active = isActive(item.path);
          const iconElement = renderIcon(item.Icon);

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={active}
                sx={{
                  borderRadius: 2,
                  color: active ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    bgcolor: active ? 'primary.dark' : 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: active ? 'white' : 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  {iconElement}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: active ? 600 : 400,
                    fontSize: '0.95rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* User Profile Section */}
      <Box sx={{ p: 2 }}>
        {tenants.length > 1 && tenantId && (
          <Select
            fullWidth
            size="small"
            value={tenantId}
            onChange={(event: SelectChangeEvent) => switchTenant(event.target.value)}
            sx={{
              mb: 1,
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.08)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.18)',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.name}
              </MenuItem>
            ))}
          </Select>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
            }}
          >
            {user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap color="white">
              {user?.email?.split('@')[0]}
            </Typography>
            <Chip
              label={role?.toUpperCase()}
              size="small"
              color={getRoleColor(role || undefined)}
              sx={{ height: 20, fontSize: '0.65rem', mt: 0.5 }}
            />
          </Box>
        </Box>
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton
            onClick={handleSignOut}
            sx={{
              borderRadius: 2,
              color: '#ff5252',
              '&:hover': {
                bgcolor: 'rgba(255, 82, 82, 0.08)',
                color: '#ff1744',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              {renderIcon(LogoutIcon)}
            </ListItemIcon>
            <ListItemText
              primary="Sign Out"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar - Only visible on mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            bgcolor: 'white',
            color: 'text.primary',
            boxShadow: 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              {renderIcon(MenuIcon)}
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Logo"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer - Permanent on desktop, temporary on mobile */}
      <Box
        component="nav"
        sx={{
          width: { md: DRAWER_WIDTH },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: '#000',
              borderRight: '1px solid rgba(255, 255, 255, 0.12)',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: '#000',
              borderRight: '1px solid rgba(255, 255, 255, 0.12)',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'grey.50',
          pt: { xs: 8, md: 0 }, // Add padding top for mobile app bar
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
